import { useRef, useState } from 'react'
import ModalGiftCard from 'src/components/GiftCard/components/Modals/ModalGiftCard'
import ModalBlockAddToCart from 'src/components/common/Modal/ModalBlockAddToCart'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import { useCheckout } from 'src/sdk/checkout/useCheckout'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { getFeatureObject } from 'src/utils/amplitude/getFeatureObject'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { getProductQueryObject } from 'src/utils/amplitude/useProductObject'
import { isPreSale } from 'src/utils/pdp/isPreSale'
import { useMobile } from 'src/hooks/useMobile'

import type { Product } from '../restructure/product/types/product'
import type { ProductItem } from '../restructure/product/types/productItem'
import {
  useAddToCartLogic,
  useBuyButtonHandler,
  useClearCartHandler,
  useSkuSelectionModal,
} from '../restructure/product/ProductCard/hooks'
import { isGiftCard } from '../restructure/product/ProductCard/utils/giftCardUtils'

type BannerProductAddToCartProps = {
  product: Product
  currentSku: ProductItem
  locationOnPage?: string
}

export function BannerProductAddToCart({
  product,
  currentSku,
  locationOnPage,
}: BannerProductAddToCartProps) {
  const {
    orderForm: { items },
    isLoading,
    clearCart,
  } = useCheckout()

  const { pageData } = useGlobalContext()
  const { isMobile } = useMobile()

  const standardObject = getStandardObject(pageData)
  const productObject = getProductQueryObject(currentSku)

  const [openModalPreSale, setOpenModalPreSale] = useState(false)
  const [openModalGiftCard, setOpenModalGiftCard] = useState(false)
  const cartWasClearedRef = useRef(false)

  const hasMultipleItems = product.items.length > 1
  const currentProductIsGiftCard = isGiftCard(product.productId)
  const cartContainsGiftCard = items?.some((item) =>
    isGiftCard(item?.productId)
  )

  const cartContainsPreSaleItem = items?.some((item) =>
    isPreSale(item.preSaleDate)
  )

  const { addToCartFn } = useAddToCartLogic({
    product,
    currentSku,
    cartWasClearedRef,
    title: 'Banner informações do produto',
    eventSource: 'banner product infos',
  })

  const { openSkuSelectionModal } = useSkuSelectionModal({
    product,
    currentSku,
    Images: currentSku.images,
    isLoading,
    addToCartFn,
    isMobile,
    standardObject,
    productObject,
  })

  const handleBuyButton = useBuyButtonHandler({
    currentSku,
    hasMultipleItems,
    currentProductIsGiftCard,
    cartContainsGiftCard,
    cartContainsPreSaleItem,
    standardObject,
    productObject,
    openSkuSelectionModal,
    addToCartFn,
    setOpenModalGiftCard,
    setOpenModalPreSale,
    slug: product.linkText,
  })

  const handleClearCartAndContinue = useClearCartHandler({
    hasMultipleItems,
    currentSku,
    cartWasClearedRef,
    clearCart,
    setOpenModalGiftCard,
    setOpenModalPreSale,
    openSkuSelectionModal,
    addToCartFn,
    standardObject,
    productObject,
  })

  const sendFeedbackClicked = () => {
    dispatchAmplitudeEvent({
      eventName: 'Promotional Element Selected',
      eventData: {
        ...standardObject,
        ...getFeatureObject({
          section: 'Informações do Produto',
          locationOnPage: locationOnPage ?? null,
          name: 'Informações do Produto',
          index: 0,
        }),
      },
    })
  }

  return (
    <>
      <button
        type="button"
        className="mt-5 flex w-full items-center justify-center rounded-full border border-white px-5 py-[1.1563rem] md:py-[.25rem]  sm:text-body-md md:text-body-sm font-semibold text-white"
        data-fs-button="add-to-cart"
        onClick={(e) => {
          sendFeedbackClicked()
          handleBuyButton(e)
        }}
        aria-label={`Adicionar ${currentSku.name} ao carrinho`}
      >
        Adicionar ao carrinho
      </button>

      <ModalGiftCard
        openModalGiftCard={openModalGiftCard}
        setOpenModalGiftCard={setOpenModalGiftCard}
        mainMessage="Não é possível comprar um Cartão Presente junto com outros produtos."
        subMessage="Deseja limpar seu carrinho para adicionar este produto ao carrinho?"
        buttonLabel="Limpar e adicionar ao carrinho"
        cancelLabel="Cancelar"
        onClearCart={handleClearCartAndContinue}
      />

      <ModalBlockAddToCart
        openModal={openModalPreSale}
        setOpenModal={setOpenModalPreSale}
        mainMessage="Não é possível comprar um produto em pré venda junto com outros produtos."
        subMessage="Deseja limpar seu carrinho para continuar com a compra?"
        buttonLabel="Limpar carrinho e continuar"
        cancelLabel="Cancelar"
        onClearCart={handleClearCartAndContinue}
      />
    </>
  )
}
